import { Formik } from 'formik';
import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Button, Buttons, CancelButton, Confirmation, Field, Form, ModalCard, Radio } from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { PageLoader } from '~/routes/public/pages';
import { emptyStringToNull, mergeValues } from '~/utils';

export default function ProjectMemberBillRateModal({ project, projectMember, onSaved, onClose }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const toast = useToast();
  const confirmation = useConfirmation();

  const projectMemberId = projectMember.id;

  const [member, setMember] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.www
          .workspaces(workspace.id)
          .projectSettings(project.id)
          .members(projectMemberId)
          .get();
        setMember(data);
      } catch {
        // Keep the modal in its loading state.
      }
    })();
  }, [api, workspace.id, project.id, projectMemberId]);

  const initialValues = mergeValues(
    { isBillable: project.isBillable, rate: '' },
    _.pick(member ?? {}, ['isBillable', 'rate']),
  );

  const handleSubmit = async (values, formik) => {
    values = emptyStringToNull(values);

    if (member.hasTimeEntries && (member.isBillable !== values.isBillable || member.rate !== values.rate)) {
      const confirm = await confirmation.prompt((resolve) => (
        <Confirmation resolve={resolve}>
          You have changed the bill rate and this member already has existing time entries. If you want those existing
          time entries to use the new rate, click OK below. If you do not want the rate to change on the existing time
          entries, click Cancel.
        </Confirmation>
      ));

      if (!confirm) return;
    }

    formik.setSubmitting(true);

    try {
      await api.www.workspaces(workspace.id).projectSettings(project.id).members(projectMemberId).upsert(values);
      toast.success('Project Member saved successfully.');
      await onSaved();
      onClose();
    } catch (error) {
      toast.error(error.message);
      formik.setSubmitting(false);
    }
  };

  return (
    <ModalCard title="Edit Bill Rate" byline={projectMember.member?.name} onClose={onClose}>
      {!member ? (
        <ModalCard.Body>
          <PageLoader />
        </ModalCard.Body>
      ) : (
        <Formik
          enableReinitialize
          initialValues={initialValues}
          onSubmit={handleSubmit}
          validateOnBlur={false}
          validateOnChange={false}
          validationSchema={Yup.object().shape({
            isBillable: Yup.bool(),
            rate: Yup.number().label('Bill Rate').min(0).max(99999999999).nullable(),
          })}>
          {({ values, ...formik }) => (
            <Form>
              <ModalCard.Body>
                <Form.Control>
                  <Field.RadioGroup name="isBillable">
                    <Radio value={true} label="Billable" />
                    <Radio value={false} label="Non-Billable" />
                  </Field.RadioGroup>
                </Form.Control>

                {values.isBillable && (
                  <Form.Control>
                    <Field.Currency name="rate" placeholder="Bill Rate" precision={7} currency={project.currency} />
                  </Form.Control>
                )}
              </ModalCard.Body>

              <ModalCard.Footer>
                <Buttons align="right">
                  <CancelButton onClick={onClose}>Close</CancelButton>

                  <Button isLoading={formik.isSubmitting} onClick={formik.submitForm}>
                    Save &amp; Close
                  </Button>
                </Buttons>
              </ModalCard.Footer>
            </Form>
          )}
        </Formik>
      )}
    </ModalCard>
  );
}
